(() => {
  'use strict';

  const storageKey = 'fitmax:quote-request-products';
  const defaultContactUrl = '/pages/preventivi';
  const quoteAnchor = 'richiesta-preventivo';

  const readItems = () => {
    try {
      const items = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      return [];
    }
  };

  const writeItems = (items) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(items.slice(0, 50)));
    } catch (error) {
      // If storage is blocked, keep the page usable without persisting.
    }
  };

  const itemKey = (item) => `${item.productId || ''}:${item.variantId || 'default'}`;

  const parseQuantity = (value) => {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const quantityInputFromButton = (button) => {
    const productRoot = button.closest('.js-product');
    return productRoot ? productRoot.querySelector('input[name="quantity"]') : null;
  };

  const absoluteUrl = (url) => {
    if (!url) return '';

    try {
      return new URL(url, window.location.origin).href;
    } catch (error) {
      return url;
    }
  };

  const itemUrl = (item) => {
    const url = absoluteUrl(item.url);
    if (!url || !item.variantId) return url;

    try {
      const parsedUrl = new URL(url);
      parsedUrl.searchParams.set('variant', item.variantId);
      return parsedUrl.href;
    } catch (error) {
      return url;
    }
  };

  const withQuoteAnchor = (url) => {
    const targetUrl = url || defaultContactUrl;

    try {
      const parsedUrl = new URL(targetUrl, window.location.origin);
      parsedUrl.hash = quoteAnchor;
      return parsedUrl.href;
    } catch (error) {
      return `${targetUrl.split('#')[0]}#${quoteAnchor}`;
    }
  };

  const goToQuoteForm = (button) => {
    const targetUrl = withQuoteAnchor(button.dataset.quoteContactUrl || defaultContactUrl);

    try {
      const parsedTarget = new URL(targetUrl, window.location.origin);
      const currentUrl = new URL(window.location.href);

      if (parsedTarget.origin === currentUrl.origin && parsedTarget.pathname === currentUrl.pathname) {
        const anchor = document.getElementById(quoteAnchor);
        if (anchor) {
          window.history.pushState(null, '', `${parsedTarget.pathname}${parsedTarget.search}${parsedTarget.hash}`);
          anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
      }
    } catch (error) {
      // Fall back to a regular navigation below.
    }

    window.location.href = targetUrl;
  };

  const buttonText = (button) => button.querySelector('[data-quote-button-text]') || button;

  const itemFromButton = (button) => {
    const quantityInput = quantityInputFromButton(button);

    return {
      productId: button.dataset.quoteProductId || '',
      variantId: button.dataset.quoteVariantId || '',
      title: button.dataset.quoteTitle || '',
      variantTitle: button.dataset.quoteVariantTitle || '',
      sku: button.dataset.quoteSku || '',
      vendor: button.dataset.quoteVendor || '',
      url: button.dataset.quoteUrl || '',
      image: button.dataset.quoteImage || '',
      quantity: parseQuantity(quantityInput ? quantityInput.value : button.dataset.quoteQuantity || 1)
    };
  };

  const formatItemsForForm = (items) => items.map((item, index) => {
    const lines = [`${index + 1}. ${item.title}`];

    lines.push(`Quantita: ${parseQuantity(item.quantity)}`);
    if (item.vendor) lines.push(`Brand: ${item.vendor}`);
    if (item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') {
      lines.push(`Variante: ${item.variantTitle}`);
    }
    if (item.sku) lines.push(`SKU: ${item.sku}`);
    if (itemUrl(item)) lines.push(`URL: ${itemUrl(item)}`);

    return lines.join('\n');
  }).join('\n\n');

  const renderContactLists = (items) => {
    document.querySelectorAll('[data-quote-request-field]').forEach((field) => {
      field.value = formatItemsForForm(items);
    });

    document.querySelectorAll('[data-quote-request-form]').forEach((container) => {
      const list = container.querySelector('[data-quote-request-list]');
      const empty = container.querySelector('[data-quote-request-empty]');
      const clear = container.querySelector('[data-quote-request-clear]');

      container.hidden = items.length === 0;
      if (empty) empty.hidden = items.length !== 0;
      if (clear) clear.hidden = items.length === 0;
      if (!list) return;

      list.textContent = '';

      items.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'quote-request-line';

        if (item.image) {
          const image = document.createElement('img');
          image.className = 'quote-request-line__image';
          image.src = item.image;
          image.alt = '';
          image.loading = 'lazy';
          row.appendChild(image);
        }

        const content = document.createElement('div');
        content.className = 'quote-request-line__content';

        const title = document.createElement('p');
        title.className = 'quote-request-line__title mb-1';
        title.textContent = item.title;
        content.appendChild(title);

        const details = [];
        if (item.variantTitle && item.variantTitle.toLowerCase() !== 'default title') {
          details.push(item.variantTitle);
        }
        if (item.sku) details.push(`SKU ${item.sku}`);

        if (details.length > 0) {
          const meta = document.createElement('p');
          meta.className = 'quote-request-line__meta text-sm mb-0';
          meta.textContent = details.join(' - ');
          content.appendChild(meta);
        }

        row.appendChild(content);

        const quantityWrap = document.createElement('label');
        quantityWrap.className = 'quote-request-line__quantity';

        const quantityText = document.createElement('span');
        quantityText.className = 'quote-request-line__quantity-label text-sm';
        quantityText.textContent = 'Quantita';
        quantityWrap.appendChild(quantityText);

        const quantityInput = document.createElement('input');
        quantityInput.type = 'number';
        quantityInput.className = 'input quote-request-line__quantity-input';
        quantityInput.min = '1';
        quantityInput.value = parseQuantity(item.quantity);
        quantityInput.dataset.quoteQuantity = itemKey(item);
        quantityWrap.appendChild(quantityInput);
        row.appendChild(quantityWrap);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'quote-request-line__remove link';
        remove.dataset.quoteRemove = itemKey(item);
        remove.textContent = 'Rimuovi';
        row.appendChild(remove);

        list.appendChild(row);
      });
    });
  };

  const syncUi = () => {
    const items = readItems();
    const keys = new Set(items.map(itemKey));
    const count = items.length;

    document.querySelectorAll('[data-quote-request-count]').forEach((counter) => {
      counter.textContent = count;
    });

    document.querySelectorAll('[data-quote-request-link]').forEach((link) => {
      link.hidden = count === 0;
      link.setAttribute(
        'aria-label',
        count === 1 ? 'Richiedi preventivo: 1 prodotto' : `Richiedi preventivo: ${count} prodotti`
      );
    });

    document.querySelectorAll('[data-quote-request-view]').forEach((link) => {
      link.hidden = count === 0;
    });

    document.querySelectorAll('.js-quote-request-add').forEach((button) => {
      const item = itemFromButton(button);
      const inRequest = keys.has(itemKey(item));
      const label = inRequest
        ? button.dataset.quoteAddedLabel || 'Già nel preventivo'
        : button.dataset.quoteLabel || 'Richiedi preventivo';

      button.classList.toggle('is-in-quote-request', inRequest);
      button.setAttribute('aria-pressed', inRequest ? 'true' : 'false');
      buttonText(button).textContent = label;
    });

    renderContactLists(items);
  };

  const showFeedback = (button, alreadyAdded) => {
    const scope = button.closest('.quote-request');
    const feedback = scope ? scope.querySelector('[data-quote-request-feedback]') : null;
    if (!feedback) return;

    feedback.textContent = alreadyAdded
      ? 'Prodotto gia presente nella richiesta preventivo.'
      : 'Prodotto aggiunto alla richiesta preventivo.';
    feedback.hidden = false;

    window.clearTimeout(showFeedback.timer);
    showFeedback.timer = window.setTimeout(() => {
      feedback.hidden = true;
    }, 3500);
  };

  const addItem = (button) => {
    const item = itemFromButton(button);
    if (!item.productId) return false;

    const items = readItems();
    const key = itemKey(item);
    const existingIndex = items.findIndex((entry) => itemKey(entry) === key);
    const alreadyAdded = existingIndex !== -1;
    const hasQuantityInput = Boolean(quantityInputFromButton(button) || button.dataset.quoteQuantity);

    if (alreadyAdded) {
      items[existingIndex] = {
        ...items[existingIndex],
        ...item,
        quantity: hasQuantityInput ? item.quantity : parseQuantity(items[existingIndex].quantity)
      };
    } else {
      items.push(item);
    }

    writeItems(items);
    showFeedback(button, alreadyAdded);
    syncUi();
    return true;
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('.js-quote-request-add');
    if (addButton) {
      event.preventDefault();
      if (addItem(addButton)) {
        goToQuoteForm(addButton);
      }
      return;
    }

    const removeButton = event.target.closest('[data-quote-remove]');
    if (removeButton) {
      event.preventDefault();
      writeItems(readItems().filter((item) => itemKey(item) !== removeButton.dataset.quoteRemove));
      syncUi();
      return;
    }

    const clearButton = event.target.closest('[data-quote-request-clear]');
    if (clearButton) {
      event.preventDefault();
      writeItems([]);
      syncUi();
    }
  });

  document.addEventListener('change', (event) => {
    const quantityInput = event.target.closest('[data-quote-quantity]');
    if (!quantityInput) return;

    const items = readItems().map((item) => {
      if (itemKey(item) !== quantityInput.dataset.quoteQuantity) return item;
      return { ...item, quantity: parseQuantity(quantityInput.value) };
    });

    writeItems(items);
    syncUi();
  });

  document.addEventListener('submit', (event) => {
    if (event.target.querySelector('[data-quote-request-field]')) {
      renderContactLists(readItems());
    }
  });

  document.addEventListener('on:variant:change', (event) => {
    const productRoot = event.target.closest('.js-product');
    const variant = event.detail ? event.detail.variant : null;
    const product = event.detail ? event.detail.product : null;
    if (!productRoot) return;

    productRoot.querySelectorAll('.js-quote-request-add').forEach((button) => {
      button.disabled = !variant;
      if (!variant) return;

      button.dataset.quoteVariantId = variant.id || '';
      button.dataset.quoteVariantTitle = variant.title || '';
      button.dataset.quoteSku = variant.sku || '';
      if (product && product.title) button.dataset.quoteTitle = product.title;
    });

    syncUi();
  });

  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('[data-quote-request-success]')) {
      writeItems([]);
    }

    document.querySelectorAll('[data-quote-contact-url]').forEach((button) => {
      const contactUrl = withQuoteAnchor(button.dataset.quoteContactUrl || defaultContactUrl);
      document.querySelectorAll('[data-quote-request-link]').forEach((link) => {
        link.href = contactUrl;
      });
    });

    syncUi();
  });
})();
